import type { MouseEvent } from "react";

import {
  RIDE_METADATA_EDITABLE_FIELDS,
  type TRideShiftTimeBackendCheckSkippedByAllowOverride,
} from "../constants";
import type {
  IPassengerInfo,
  IRideDto,
  IRideMetadata,
  IRideTableDto,
  IStationInfo,
  TStationComposedId,
} from "../dto";
import type {
  CourseRouteTypeEnum,
  DirectionEnum,
  DirectionsServiceEnum,
  EditFormMode,
  PassengerDeleteResultEnum,
  PriceRateSearchExplainMode,
  RebuildRouteTypeEnum,
  RecalculatePriceMode,
  RideDelayStatusEnum,
  RideDeleteActionsResultEnum,
  RideFetchDataMode,
  RideSoftValidationErrorType,
  RideStationPanelMode,
  RideStatusEnum,
  ShiftTimeValidationDebugStatusEnum,
  SuperAdminPriceMode,
} from "../enums";
import type { ILocationInfo, RydeUiUnit } from "./common.models";
import type { IRichLogRecord } from "./rich-log";
import type {
  IBulkSoftValidationErrorBaseResponse,
  IBulkSoftValidationItemErrorBase,
  ISoftValidationErrorBase,
  ISoftValidationErrorBaseResponse,
} from "./soft-validation.models";
import type { IStationRouteResult } from "./station-route.models";
import type { ITrackingRenderState } from "./tracking.models";

export interface IRideAreasSnapshot {
  areaFromId: number | undefined;
  areaToId: number | undefined;
}

/** Counts for ride drawer Events tab label `(new/total)`. */
export interface IRideIncidentsTabCounts {
  newCount: number;
  total: number;
}

export interface IPassengerMove {
  id: number;
  seats: number;
  fromStationId: number;
  toStationId: number;
  bookingId?: number;
  shiftHour?: TDateISO;
}

export interface IRideMovePassengersApiPayload {
  fromRideId: number;
  toRideId?: number;
  passengers: IPassengerMove[];
  token?: string;
  assignCourseId?: number;
}

export interface IRideRemovePassengersApiPayload {
  passengerIds: number[];
  bookingAction?: PassengerDeleteResultEnum;
  token?: string;
}

export interface IRideRemoveEmptyStationApiPayload {
  emptyStationId: number;
  secondaryId?: number;
  skipMlPathByWaypoints?: boolean;
}

export interface IRideArchiveCancelApiPayload {
  ids: number[];
  bookingAction?: RideDeleteActionsResultEnum;
  token?: string;
  // RYDE-7633: one free-text reason for the whole selection, sent when it holds a past taxi-supplier
  // ride — those are canceled locally (no provider notification) and the backend requires the reason.
  cancelationReason?: string;
}

export interface IRideBulkRebuildRoutesPayload {
  ids: number[];
  token?: string;
  rebuildType?: RebuildRouteTypeEnum;
  provider?: DirectionsServiceEnum;
  avoidTolls?: boolean;
  skipCachedPaths?: boolean;
  skipMlPath?: boolean;
}

export interface IBulkRideRebuildPayload {
  ids: number[];
  isIncludePastRides: boolean;
  isIncludeSentRides: boolean;
  isRecalculateCoordinates: boolean;
}

export interface IBulkUpdateStationsPayload {
  ids: number[];
  firstStationId?: number;
  lastStationId?: number;
}

export interface IRideBulkUpdatePayload {
  ids: number[];
  payload: Partial<IRideTableDto>;
  token?: string;
}

export interface IRideRecalculatePricePayload {
  ride: Partial<IRideDto>;
  isPassengerChanged?: boolean;
  isProduction?: boolean;
  lockSupplier?: boolean;
  lockVehicleType?: boolean;
  lockCurrentPriceRateIfExists?: boolean;
  mode?: RecalculatePriceMode;
  adminUiMode?: SuperAdminPriceMode;
  token?: string;
}

export interface IPriceRateSearchCurrentRateInfo {
  id: number;
  name: string | null;
  label: string | null;
  supplierId: number | null;
  supplierName: string | null;
  vehicleTypeId: number | null;
  vehicleTypeName: string | null;
  maxDuration: number | null;
  type: number | null;
  sourceAreaId: number | null;
  sourceAreaName: string | null;
  destinationAreaId: number | null;
  destinationAreaName: string | null;
  minPrice: number | null;
}

export interface IPriceRateSearchRideContext {
  supplierId: number | null;
  vehicleTypeId: number | null;
  priceRateId: number | null;
  currentPriceRate?: IPriceRateSearchCurrentRateInfo | null;
  direction?: string | null;
  vehicleTypeMaxPlaces?: number | null;
  geoProdMaxPlaces?: number | null;
  geoProdMaxPlacesB?: number | null;
}

export interface IPriceRateSearchDetailsPayload {
  rideId: number;
  explainMode?: PriceRateSearchExplainMode;
  isProduction?: boolean;
  supplierId?: number | null;
  vehicleTypeId?: number | null;
  lockSupplier?: boolean;
  lockVehicleType?: boolean;
  lockCurrentPriceRateIfExists?: boolean;
}

export interface IPriceRateSearchPriceAddition {
  label: string;
  value: number;
}

export interface IPriceRateSearchMatchedSummary {
  id: number;
  name: string;
  basePrice: number | null;
  price: number;
  additions: IPriceRateSearchPriceAddition[];
  rate?: IPriceRateSearchCurrentRateInfo | null;
}

export interface IPriceRateSearchDetailsResult {
  logs: IRichLogRecord[];
  matchedPriceRateId: number | null;
  matchedPriceRate?: IPriceRateSearchMatchedSummary | null;
  rideContext?: IPriceRateSearchRideContext;
}

export interface IRideRebuildPayload {
  ride: Partial<IRideDto>;
  rebuildType?: RebuildRouteTypeEnum;
  optimize?: boolean;
  provider?: DirectionsServiceEnum;
  token?: string;
}

export interface IRideBulkRecalculatePricePayload {
  ids: number[];
  includeManualPrices?: boolean;
  shouldClearManualPriceAndSetDraftIfNoRate?: boolean;
  recalculateDoneRides?: boolean;
  allowSupplierChange?: boolean;
  allowVehicleTypeChange?: boolean;
  allowPriceRateChange?: boolean;
}

export interface IRideCancelApiPayload {
  bookingAction?: RideDeleteActionsResultEnum;
  token?: string;
  // RYDE-7633: free-text reason, sent only when cancelling a past taxi-supplier ride locally.
  cancelationReason?: string;
}

export type IRideArchiveApiPayload = IRideCancelApiPayload;

export interface IRideAssignDocksPayload {
  rideIds: number[];
  dataMode?: RideFetchDataMode;
}

export interface IReorderStationsPayload {
  rideId: number;
  stationComposedIds: TStationComposedId[];
  isReturnDtoForGrid?: boolean;
  token?: string;
}

export type DeletePassengerHandlerType = (
  e: MouseEvent,
  hoverId?: number,
) => Promise<void>;

export interface IRideUpdateResultDto {
  rides: IRideTableDto[];
}

export interface IRideStatusUpdate {
  id: number;
  status: RideStatusEnum;
  /** The ride's stored update time; the server sends it with live status pushes. */
  updatedAt?: TDateISO;
}

export interface IAppliedRideStatusUpdates<T> {
  updatedRows: T[];
  hasMatchedRows: boolean;
}

export interface INearbySearchResultDto {
  ridesIds: number[];
}

export interface IUpdateRideStationDto {
  stationId: number;
  isLocked?: boolean;
  comment?: string;
}

export interface IPassengerDeleteModalPayload {
  mode: RideStationPanelMode;
  ride: IRideDto;
  passengers: IPassengerInfo[];
  resolveFn: (value: PassengerDeleteResultEnum) => void;
}

export interface IRideArchiveModalPayload {
  resolveFn: (value: RideDeleteActionsResultEnum) => void;
}
export interface IRideCancelModalResult {
  bookingAction: RideDeleteActionsResultEnum;
  // RYDE-7633: operator's free-text reason, collected only when requireCancelationReason is set.
  cancelationReason?: string;
}

export interface IRideCancelModalPayload {
  rideIds: number[];
  // RYDE-7633: when true, the cancel modal shows a free-text reason input (past taxi-supplier ride).
  requireCancelationReason?: boolean;
  resolveFn: (result: IRideCancelModalResult) => void;
}

export type RideMetadataDirty = Record<
  keyof IRideMetadata,
  boolean | undefined
>;

export interface ICarTrackingInfo {
  rideId: number;
  metadata?: IRideMetadata;
  supplierName?: string;
  driverName?: string;
  driverPhone?: string;
  externalDriverName?: string;
  externalDriverPhone?: string;
  vehicleName?: string;
  coords?: ILocationInfo;
  plannedArrivalTime?: TDateISO;
  actualArrivalTime?: TDateISO;
  timeLate?: number;
  statusLate?: RideDelayStatusEnum;
  destinationStationName?: string;
  rideTypeName?: string;
  courseId?: number | null;
  courseName?: string;
  courseCode?: string;
  courseRouteType?: CourseRouteTypeEnum;
  courseLineColor?: string;
  scheduleNumber?: string;
  passengersCount?: number;
  trackingLastUpdate?: TDateISO;
  vehicleTypeName?: string;
  originStationName?: string;
  siteName?: string;
}

export interface IRideRoute {
  rideId?: number | undefined;
  rideStatus?: RideStatusEnum | undefined;
  courseId?: number | undefined;
  /** When set (from ride `computedData.courseLineColor`), map polyline uses this instead of palette-by-rideId. */
  courseLineColor?: string | undefined;
  stationRouteId?: number | undefined;
  locations?: ILocationInfo[];
  routePolyline?: string;
  carTrackingInfo?: ICarTrackingInfo;
  isTrackingRoute?: boolean;
  direction?: DirectionEnum;
  /** Course metadata: user drew the polyline — keep it on the map while stations are edited. */
  isManualPolylineEdit?: boolean;
  /** Precomputed GPS trail geometry — avoids rebuilding from DTOs on every poll. */
  trackingRenderState?: ITrackingRenderState;
}

export interface IRideStationsBulkUpdateParams {
  rideDto: Partial<IRideDto>;
  passengerIds: number[];
  stationFromId: number | undefined;
  stationToId: number | undefined;
}

export interface IRideStationsBulkUpdateRecountData {
  stationsInfos: IStationInfo[];
}

export interface IMoveActionHardRulesError {
  translationKey: string;
  metadata: {
    timeDiff: number;
    allowed: number;
  };
}

export interface IMoveActionValidateRouteBadRequestError {
  translationKey: string;
}

export type RideSoftValidationErrorAdditionalKeys =
  | "flexRideId"
  | "rideId"
  | "oldPrice"
  | "newPrice"
  | "newDuration"
  | "maxDuration"
  | "newTimeDiff"
  | "allowedPassengerDurationRatio"
  | "forPassengerDisplayInfo"
  | "newDepartureGapDiffMinutes"
  | "departureTimeOld"
  | "departureTimeNew"
  | "passengerInfo"
  | "allowedGeoProdMaxShiftGap"
  | "newArrivalGapDiffMinutes"
  | "arrivalTimeOld"
  | "arrivalTimeNew"
  | "maximumNumberOfStops"
  | "newNumbersOfStops"
  | "canceledRideId"
  | "oldBothRidePrice"
  | "savedPrice"
  | "oldTargetRidePrice"
  | "diffPrice"
  | "stationName"
  | "times"
  | "shiftGapMinutes"
  | "passengerCombinedInfo"
  | "relatedRideIds";

export type RideSoftValidationError = ISoftValidationErrorBase<
  RideSoftValidationErrorType,
  RideSoftValidationErrorAdditionalKeys
>;
export type RideSoftValidationErrorResponse = ISoftValidationErrorBaseResponse<
  RideSoftValidationErrorType,
  RideSoftValidationErrorAdditionalKeys
>;
export type RideBulkSoftValidationItemError = IBulkSoftValidationItemErrorBase<
  RideSoftValidationErrorType,
  RideSoftValidationErrorAdditionalKeys
>;
export type TRideBulkSoftValidationErrorResponse =
  IBulkSoftValidationErrorBaseResponse<
    RideSoftValidationErrorType,
    RideSoftValidationErrorAdditionalKeys
  >;

export const RideFormFields = {
  id: "id",
  direction: "direction",
  rideStart: "rideStart",
  status: "status",
  rideEnd: "rideEnd",
  supplierId: "supplierId",
  supplierName: "supplierName",
  vehicleTypeId: "vehicleTypeId",
  vehicleTypeName: "vehicleTypeName",
  rideTypeId: "rideTypeId",
  rideTypeName: "rideTypeName",
  remarks: "metadata.remarks",
  tags: "metadata.tags",
  invoice: "metadata.invoice",
  duplicationsNumber: "metadata.duplicationsNumber",
  approver: "metadata.approver",
  title: "metadata.title",
  manualPrice: "metadata.manualPrice",
  priceDetails: "metadata.priceDetails",
  supplierPriceDetails: "metadata.supplierPriceDetails",
  supplierManualPrice: "metadata.supplierManualPrice",
  priceRateAdditions: "metadata.priceRateAdditions",
  reasonId: "reasonId",
  additionIds: "additionIds",
} satisfies Record<string, TRideFieldName | TRideJoinedTableFieldName>;

export type TRideDtoStartEndKey =
  typeof RideFormFields.rideStart | typeof RideFormFields.rideEnd;

export interface IRideManualChangeStartEndInfo {
  fieldChanged: TRideDtoStartEndKey;
  value: TDateISO;
}

export type TRideFieldName =
  | "id"
  | "status"
  | "rideStart"
  | "rideEnd"
  | "direction"
  | "supplierId"
  | "rideTypeId"
  | "vehicleTypeId"
  | "vehicleId"
  | "driverId"
  | "areaIds"
  | "siteId"
  | "metadata.manualPrice"
  | "metadata.priceDetails"
  | "metadata.supplierPriceDetails"
  | "metadata.supplierManualPrice"
  | "metadata.priceRateAdditions"
  | "additionIds"
  | "reasonId"
  | "metadata.approver"
  | "metadata.title"
  | "metadata.remarks"
  | "metadata.tags"
  | "metadata.invoice"
  | "metadata.duplicationsNumber";

export type TRideJoinedTableFieldName =
  "supplierName" | "vehicleTypeName" | "rideTypeName";

export interface IRideSaveRoutePayload {
  id?: number | null;
  date?: TDateISO;
  rideId: number | undefined;
  direction?: DirectionEnum;
  route: IStationRouteResult;
}

export interface IRideSaveRouteResponse {
  stationRouteId: number;
}

export interface IDeleteStationRoutePayload {
  rideId: number | undefined;
  stationRouteId: number;
}

export interface ICheckStationRouteExistsPayload {
  stationRouteId: number;
}

export interface IConvertStationRouteToCustomizedPayload {
  stationRouteId: number;
  /** User confirmed deleting the customized ML path already saved for this station set. */
  shouldReplaceExisting?: boolean;
}

export interface IConvertStationRouteToCustomizedResponse {
  stationRouteId: number;
}

export interface ICheckStationRoutePausedPayload {
  rideId: number;
  stationRouteId: number;
}

export interface IMatchingMlPathsPayload {
  stationIds: number[];
  avoidTolls?: boolean;
  preferredId?: number | null;
}

export interface IMatchingMlPathsResponse {
  ids: number[];
  updateId: number | null;
}

export type TRideCrudModeKey = {
  uiUnit: RydeUiUnit;
  formMode: EditFormMode;
};

export type TRideMetadataFields = keyof IRideMetadata;
export type TRideMetadataEditableField =
  (typeof RIDE_METADATA_EDITABLE_FIELDS)[number];

export const isRideMetadataEditableField = (
  fieldName: TRideMetadataFields,
): fieldName is TRideMetadataEditableField => {
  if (RIDE_METADATA_EDITABLE_FIELDS.includes(fieldName)) {
    return true;
  }

  return false;
};

export type RideUpdateDriverOrVehicleFieldName = Extract<
  TRideFieldName,
  "driverId" | "vehicleId"
>;

export type RideUpdateDriverOrVehiclePatch = {
  [K in RideUpdateDriverOrVehicleFieldName]?: number | null;
};

export type TRideUpdateDriverOrVehicleDto = Partial<
  RideUpdateDriverOrVehiclePatch & {
    isReturnDtoForGrid?: boolean;
    token?: string;
  }
>;

export interface IShiftTimeValidationDebugEntry {
  check: TRideShiftTimeBackendCheckSkippedByAllowOverride;
  status: ShiftTimeValidationDebugStatusEnum;
  detail: string;
}

export interface IShiftTimeValidationDebugInfo {
  checks: IShiftTimeValidationDebugEntry[];
}
